import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

const COOKIE_NAME = 'affiliate_link_hash';
const COOKIE_DAYS = 30;
const SESSION_TRACKED_KEY_PREFIX = 'aff_tracked_';

/**
 * AffiliateTrackingService — Task 9 & Direct Link Support
 *
 * Responsibilities:
 * 1. On app startup: detect ?ref_hash=XYZ in URL, write a 30-day cookie (Last Click Wins),
 *    and trigger an asynchronous click tracking call with engagement delay & session deduplication.
 * 2. At checkout: expose getTrackingHash() so cart service can attach the hash to the order payload.
 *
 * Cookie strategy:
 * - Always overwrite with the latest ref_hash (Last Click Wins).
 * - Cookie is cleared when the user places an order (optional, called from checkout).
 * - Secure; SameSite=Lax — compatible with most browsers and HTTPS setups.
 */
@Injectable({
  providedIn: 'root',
})
export class AffiliateTrackingService {
  private http = inject(HttpClient);

  /**
   * Should be called once on AppComponent.ngOnInit().
   * Reads ?ref_hash from the current URL, stores it as a 30-day cookie,
   * and fires an engagement-delayed click tracking request if not yet tracked in this session.
   */
  captureFromUrl(): void {
    const params = new URLSearchParams(window.location.search);
    const hash = params.get('ref_hash');
    if (hash && /^[A-Za-z0-9]{4,12}$/.test(hash)) {
      let hashes = this.getTrackingHashes();
      // Remove it if it already exists so we can move it to the end (most recent click)
      hashes = hashes.filter(h => h !== hash);
      hashes.push(hash);
      // Keep only the most recent 10 hashes to prevent cookie bloat
      if (hashes.length > 10) {
        hashes.shift();
      }
      this.setCookie(JSON.stringify(hashes));

      // Asynchronous click tracking with session deduplication & engagement delay (anti-spam)
      this.trackClickWithEngagementDelay(hash);
    }
  }

  /**
   * Sends a lightweight click tracking request after a brief engagement delay.
   * Deduplicates within the current browser session so refreshing (F5) doesn't spam requests.
   */
  private trackClickWithEngagementDelay(hash: string): void {
    try {
      const sessionKey = `${SESSION_TRACKED_KEY_PREFIX}${hash}`;
      if (environment.production && sessionStorage.getItem(sessionKey)) {
        return; // Already tracked in this browser session (production only)
      }
      sessionStorage.setItem(sessionKey, '1');

      // 2-second engagement delay in production; 500ms in dev
      const delay = environment.production ? 2000 : 500;
      setTimeout(() => {
        const trackUrl = `${environment.apiURL}/api/go/track/${hash}`;
        this.http.post(trackUrl, {}).subscribe({
          next: () => {},
          error: (err) => {
            if (!environment.production) {
              console.warn('[Affiliate Tracking] Click tracking request returned error:', err);
            }
          }
        });
      }, delay);
    } catch {
      // Gracefully handle environments where sessionStorage is disabled/blocked
    }
  }

  /**
   * Returns the stored affiliate hashes.
   * Used by the checkout flow to attach the affiliate hashes to the order payload.
   */
  getTrackingHashes(): string[] {
    const value = this.getCookie();
    if (!value) return [];
    try {
      if (value.startsWith('[')) {
        return JSON.parse(value);
      }
      return [value]; // Legacy fallback
    } catch {
      return [];
    }
  }

  /**
   * Call after a successful order creation to prevent the same hash
   * being reused on the next order from the same browser session.
   * The 30-day cookie naturally expires even without this call.
   */
  clearTrackingHash(): void {
    this.deleteCookie();
  }

  // ──────────────────────────── Cookie Helpers ────────────────────────────

  private setCookie(value: string): void {
    const expires = new Date();
    expires.setDate(expires.getDate() + COOKIE_DAYS);
    document.cookie = [
      `${COOKIE_NAME}=${encodeURIComponent(value)}`,
      `expires=${expires.toUTCString()}`,
      'path=/',
      'SameSite=Lax',
      // Uncomment for HTTPS-only deployments:
      // 'Secure',
    ].join('; ');
  }

  private getCookie(): string | null {
    const match = document.cookie
      .split(';')
      .map((c) => c.trim())
      .find((c) => c.startsWith(`${COOKIE_NAME}=`));

    return match ? decodeURIComponent(match.split('=').slice(1).join('=')) : null;
  }

  private deleteCookie(): void {
    document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
  }
}
